import React, { useEffect, useState } from 'react';
import { usersAPI, invitationsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Users, 
  UserCheck, 
  UserX, 
  Clock, 
  CheckCircle,
  Shield,
  User,
  Pencil,
  Trash2,
  Plus,
  Ticket,
  Copy,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ROLE_OPTIONS = [
  { value: 'head_coach', label: 'Head Coach', icon: Shield, color: 'text-[#FFD60A]', description: 'Full access, can manage users' },
  { value: 'coordinator', label: 'Coordinator', icon: Shield, color: 'text-[#32D74B]', description: 'Full coaching access' },
  { value: 'trainer', label: 'Trainer', icon: Shield, color: 'text-[#007AFF]', description: 'Full access, injury focus' },
  { value: 'asst_coach', label: 'Asst Coach', icon: User, color: 'text-[#FF9500]', description: 'Limited access' },
  { value: 'coach', label: 'Coach', icon: User, color: 'text-[#71717A]', description: 'Basic access' },
];

export default function UserManagementPage() {
  const { user: currentUser, canInviteUsers } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('pending'); // 'pending', 'all', 'invitations'
  const [editModal, setEditModal] = useState({ open: false, user: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [inviteModal, setInviteModal] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '' });
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'coach', assigned_position: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes] = await Promise.all([
        usersAPI.getPending(),
        usersAPI.getAll()
      ]);
      setPendingUsers(pendingRes.data);
      setUsers(allRes.data);
      
      // Load invitations if user is head coach
      if (canInviteUsers) {
        try {
          const invRes = await invitationsAPI.getAll();
          setInvitations(invRes.data);
        } catch (e) {
          // Ignore if not head coach
        }
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, userName) => {
    try {
      await usersAPI.approve(userId);
      toast.success(`${userName} has been approved`);
      loadData();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleReject = async (userId, userName) => {
    try {
      await usersAPI.reject(userId);
      toast.success(`${userName} has been removed`);
      loadData();
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };

  const openEditModal = (user) => {
    setEditForm({ name: user.name, email: user.email, role: user.role });
    setEditModal({ open: true, user });
  };

  const handleEditSubmit = async () => {
    if (!editModal.user) return;
    
    try {
      await usersAPI.update(editModal.user.id, editForm);
      toast.success('User updated successfully');
      setEditModal({ open: false, user: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.user) return;
    
    try {
      await usersAPI.delete(deleteModal.user.id);
      toast.success(`${deleteModal.user.name} has been deleted`);
      setDeleteModal({ open: false, user: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreateInvitation = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      toast.error('Name and email are required');
      return;
    }
    
    try {
      const res = await invitationsAPI.create(inviteForm);
      toast.success(`Invitation created! Code: ${res.data.code}`);
      setInviteModal(false);
      setInviteForm({ name: '', email: '', role: 'coach', assigned_position: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    }
  };

  const handleDeleteInvitation = async (id) => {
    try {
      await invitationsAPI.delete(id);
      toast.success('Invitation deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invitation');
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const copyInviteLink = (code) => {
    const link = `${window.location.origin}/login?code=${code}`;
    navigator.clipboard.writeText(link);
    toast.success('Invitation link copied to clipboard');
  };

  const getRoleInfo = (role) => {
    return ROLE_OPTIONS.find(r => r.value === role) || ROLE_OPTIONS[4];
  };

  const approvedUsers = users.filter(u => u.approved !== false);

  return (
    <Layout>
      <div className="space-y-6" data-testid="user-management-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <p className="text-xs text-[#71717A] uppercase tracking-wider">Administration</p>
            <h1 className="font-display text-4xl text-white tracking-tight">USER MANAGEMENT</h1>
          </div>

          <div className="flex gap-2">
            {/* View Mode Toggle */}
            <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
              <TabsList className="bg-[#121212] border border-[#27272A]">
                <TabsTrigger 
                  value="pending" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                  data-testid="pending-tab"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Pending ({pendingUsers.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="all" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                  data-testid="all-users-tab"
                >
                  <Users className="w-4 h-4 mr-2" />
                  All Users
                </TabsTrigger>
                {canInviteUsers && (
                  <TabsTrigger 
                    value="invitations" 
                    className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                    data-testid="invitations-tab"
                  >
                    <Ticket className="w-4 h-4 mr-2" />
                    Invitations
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>

            {/* Invite Button - Only for Head Coach */}
            {canInviteUsers && (
              <Button
                onClick={() => setInviteModal(true)}
                className="bg-[#32D74B] hover:bg-[#32D74B]/90 text-white"
                data-testid="invite-user-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#121212] border-[#27272A]">
            <CardContent className="p-4 text-center">
              <p className="font-display text-3xl text-[#FF9500]">{pendingUsers.length}</p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-[#27272A]">
            <CardContent className="p-4 text-center">
              <p className="font-display text-3xl text-[#32D74B]">{approvedUsers.length}</p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Approved</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-[#27272A]">
            <CardContent className="p-4 text-center">
              <p className="font-display text-3xl text-white">{users.length}</p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Total Users</p>
            </CardContent>
          </Card>
          {canInviteUsers && (
            <Card className="bg-[#121212] border-[#27272A]">
              <CardContent className="p-4 text-center">
                <p className="font-display text-3xl text-[#007AFF]">
                  {invitations.filter(i => !i.used).length}
                </p>
                <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Active Invites</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Content based on view mode */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]"></div>
          </div>
        ) : viewMode === 'pending' ? (
          /* Pending Users */
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#FF9500]" />
                Pending Approval
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {pendingUsers.length > 0 ? (
                <div className="divide-y divide-[#27272A]">
                  {pendingUsers.map((user) => {
                    const roleInfo = getRoleInfo(user.role);
                    const RoleIcon = roleInfo.icon;
                    
                    return (
                      <div key={user.id} className="flex items-center justify-between p-4 hover:bg-[#1A1A1A]">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-[#FF9500]/10 flex items-center justify-center">
                            <Clock className="w-6 h-6 text-[#FF9500]" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.name}</p>
                            <p className="text-sm text-[#71717A]">{user.email}</p>
                            <div className="flex items-center gap-1 mt-1">
                              <RoleIcon className={`w-3 h-3 ${roleInfo.color}`} />
                              <span className={`text-xs ${roleInfo.color}`}>{roleInfo.label}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(user.id, user.name)}
                            className="bg-[#32D74B]/10 text-[#32D74B] border border-[#32D74B]/30 hover:bg-[#32D74B]/20"
                            data-testid={`approve-${user.id}`}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleReject(user.id, user.name)}
                            className="bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20"
                            data-testid={`reject-${user.id}`}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                  <CheckCircle className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg">No pending approvals</p>
                  <p className="text-sm">All users have been reviewed</p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'all' ? (
          /* All Users */
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5" />
                All Users ({users.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {users.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#27272A]">
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">User</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Role</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Status</th>
                        <th className="p-4 text-xs text-[#71717A] uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => {
                        const roleInfo = getRoleInfo(user.role);
                        const RoleIcon = roleInfo.icon;
                        const isCurrentUser = user.id === currentUser?.id;
                        
                        return (
                          <tr key={user.id} className="border-b border-[#27272A] hover:bg-[#1A1A1A]">
                            <td className="p-4">
                              <div>
                                <p className="text-white font-medium">
                                  {user.name}
                                  {isCurrentUser && <span className="text-[#71717A] text-xs ml-2">(You)</span>}
                                </p>
                                <p className="text-sm text-[#71717A]">{user.email}</p>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <RoleIcon className={`w-4 h-4 ${roleInfo.color}`} />
                                <span className={roleInfo.color}>{roleInfo.label}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              {user.approved !== false ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#32D74B]/10 text-[#32D74B] text-xs">
                                  <CheckCircle className="w-3 h-3" />
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#FF9500]/10 text-[#FF9500] text-xs">
                                  <Clock className="w-3 h-3" />
                                  Pending
                                </span>
                              )}
                            </td>
                            <td className="p-4">
                              {!isCurrentUser && (
                                <div className="flex gap-2 justify-center">
                                  <Button
                                    size="sm"
                                    onClick={() => openEditModal(user)}
                                    className="bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/30 hover:bg-[#007AFF]/20"
                                    data-testid={`edit-${user.id}`}
                                  >
                                    <Pencil className="w-3 h-3 mr-1" />
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => setDeleteModal({ open: true, user })}
                                    className="bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20"
                                    data-testid={`delete-${user.id}`}
                                  >
                                    <Trash2 className="w-3 h-3 mr-1" />
                                    Delete
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                  <Users className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg">No users yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Invitations (Head Coach Only) */
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="text-white flex items-center gap-2">
                <Ticket className="w-5 h-5 text-[#007AFF]" />
                Invitations
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {invitations.length > 0 ? (
                <div className="divide-y divide-[#27272A]">
                  {invitations.map((invite) => {
                    const roleInfo = getRoleInfo(invite.role);
                    const RoleIcon = roleInfo.icon;
                    
                    return (
                      <div key={invite.id} className="flex items-center justify-between p-4 hover:bg-[#1A1A1A]">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 flex items-center justify-center ${
                            invite.used ? 'bg-[#32D74B]/10' : 'bg-[#007AFF]/10'
                          }`}>
                            {invite.used ? (
                              <CheckCircle className="w-6 h-6 text-[#32D74B]" />
                            ) : (
                              <Ticket className="w-6 h-6 text-[#007AFF]" />
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium">{invite.name}</p>
                            <p className="text-sm text-[#71717A]">{invite.email}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <div className="flex items-center gap-1">
                                <RoleIcon className={`w-3 h-3 ${roleInfo.color}`} />
                                <span className={`text-xs ${roleInfo.color}`}>{roleInfo.label}</span>
                              </div>
                              {!invite.used && (
                                <span className="text-xs font-mono bg-[#27272A] px-2 py-0.5 text-white">
                                  {invite.code}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {invite.used ? (
                            <span className="text-sm text-[#32D74B]">
                              Used {invite.used_at ? format(new Date(invite.used_at), 'MMM d') : ''}
                            </span>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                onClick={() => copyInviteCode(invite.code)}
                                className="bg-[#27272A] hover:bg-[#3F3F46] text-white"
                                title="Copy code"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => copyInviteLink(invite.code)}
                                className="bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/30 hover:bg-[#007AFF]/20"
                                title="Copy link"
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleDeleteInvitation(invite.id)}
                                className="bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                  <Ticket className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg">No invitations yet</p>
                  <p className="text-sm">Invite coaches to join your team</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Edit User Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ ...editModal, open })}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[#A1A1AA]">Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Email</Label>
                <Input
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Role</Label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-[#27272A] text-white px-3 py-2 mt-1"
                >
                  {ROLE_OPTIONS.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditModal({ open: false, user: null })}
                className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditSubmit}
                className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              >
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ ...deleteModal, open })}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white">
            <DialogHeader>
              <DialogTitle className="text-[#FF3B30]">Delete User</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-[#A1A1AA]">
                Are you sure you want to delete <span className="text-white font-medium">{deleteModal.user?.name}</span>?
              </p>
              <p className="text-sm text-[#71717A] mt-2">
                This action cannot be undone.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteModal({ open: false, user: null })}
                className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invite User Modal */}
        <Dialog open={inviteModal} onOpenChange={setInviteModal}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#32D74B]" />
                Invite New User
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[#A1A1AA]">Full Name</Label>
                <Input
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  placeholder="John Smith"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  data-testid="invite-name-input"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Email</Label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="coach@team.com"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Role</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {ROLE_OPTIONS.filter(r => r.value !== 'head_coach').map((role) => (
                    <button
                      key={role.value}
                      type="button"
                      onClick={() => setInviteForm({ ...inviteForm, role: role.value, assigned_position: '' })}
                      className={`p-3 border flex items-center gap-2 transition-all text-left ${
                        inviteForm.role === role.value 
                          ? 'border-[#FF3B30] bg-[#FF3B30]/10' 
                          : 'border-[#27272A] bg-[#0A0A0A] hover:border-[#3F3F46]'
                      }`}
                    >
                      <role.icon className={`w-4 h-4 ${role.color}`} />
                      <div>
                        <span className="text-sm font-medium text-white">{role.label}</span>
                        <p className="text-[10px] text-[#71717A]">{role.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              {/* Position Assignment - Only for coach roles */}
              {['coach', 'asst_coach'].includes(inviteForm.role) && (
                <div>
                  <Label className="text-[#A1A1AA]">Assign Position (for Depth Chart)</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {['QB', 'RB', 'WR', 'TE', 'OL', 'DB', 'LB', 'DL'].map((pos) => (
                      <button
                        key={pos}
                        type="button"
                        onClick={() => setInviteForm({ ...inviteForm, assigned_position: inviteForm.assigned_position === pos ? '' : pos })}
                        className={`p-2 border text-center transition-all ${
                          inviteForm.assigned_position === pos 
                            ? 'border-[#FFD60A] bg-[#FFD60A]/10 text-[#FFD60A]' 
                            : 'border-[#27272A] bg-[#0A0A0A] text-white hover:border-[#3F3F46]'
                        }`}
                        data-testid={`position-${pos}-btn`}
                      >
                        {pos}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[#71717A] mt-2">
                    {inviteForm.assigned_position 
                      ? `This coach will be able to edit the ${inviteForm.assigned_position} depth chart` 
                      : 'No position assigned - coach will only view depth chart'}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setInviteModal(false)}
                className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateInvitation}
                className="bg-[#32D74B] hover:bg-[#32D74B]/90 text-white"
                data-testid="send-invite-btn"
              >
                <Ticket className="w-4 h-4 mr-2" />
                Create Invitation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
